import type { CoreVersionInfoResponse } from '@/lib/api/types';

const frontendVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

interface VersionDisplay {
  text: string;
  tooltip: string;
}

// ``getCoreVersionInfo`` swallows network errors and returns a soft
// stub shape so smoke tests stay quiet — which means TanStack Query
// never sees ``isError``. The stub is identified by its sentinel
// ``reason`` string; a healthy backend without a configured minimum
// frontend version also reports ``backend_version='unknown'`` +
// ``compatibility_status='unknown'`` (e.g. local dev) but with a
// different reason, so we can't key off those alone.
export const BACKEND_UNREACHABLE_REASON = 'Version endpoint unavailable';

export function isBackendUnreachable(
  versionInfo: CoreVersionInfoResponse | undefined,
  isError: boolean,
): boolean {
  if (isError) return true;
  if (!versionInfo) return false;
  return versionInfo.reason === BACKEND_UNREACHABLE_REASON;
}

export function getVersionDisplay(
  backendVersion: string | undefined,
  isError: boolean,
  isLoading: boolean,
): VersionDisplay {
  if (isLoading) {
    return {
      text: `v${frontendVersion}`,
      tooltip: `Frontend ${frontendVersion}, backend loading...`,
    };
  }

  if (isError) {
    return {
      text: `FE ${frontendVersion} / BE down`,
      tooltip: `Frontend ${frontendVersion}, backend is unreachable`,
    };
  }

  if (!backendVersion) {
    return {
      text: `FE ${frontendVersion} / BE unknown`,
      tooltip: `Frontend ${frontendVersion}, backend version unknown`,
    };
  }

  if (frontendVersion === backendVersion) {
    return {
      text: `v${frontendVersion}`,
      tooltip: `Frontend and backend both at ${frontendVersion}`,
    };
  }

  return {
    text: `FE ${frontendVersion} / BE ${backendVersion}`,
    tooltip: `Frontend ${frontendVersion}, Backend ${backendVersion}`,
  };
}
